import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Loader2, Send, StickyNote, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { MentionInput, renderWithMentions } from '@/components/support/MentionInput';
import { AttachmentGallery, NoteImagePicker, uploadTicketFiles } from '@/components/support/TicketAttachments';

export const NOTE_TEMPLATES = [
  'Called customer — no answer, left voicemail.',
  'Called customer — spoke to them, ',
  'Chased supplier for an update.',
  'Replacement sent — tracking: ',
  'Awaiting photos from customer.',
  'Refund processed.',
  'Booked installer visit for ',
];

/** One-click phrases that drop into the note box (cursor left at the end so you can finish the sentence). */
export function NoteTemplateChips({ onPick, testid = 'note-template' }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-chips`}>
      {NOTE_TEMPLATES.map((t) => (
        <button key={t} type="button" onClick={() => onPick(t)} className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100 hover:border-slate-300" data-testid={`${testid}-${t.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '')}`}>
          {t.replace(/[—:,]\s*$/, '').trim()}
        </button>
      ))}
    </div>
  );
}

/** Full notes composer: templates + @mentions + images. Calls onAdded(note, uploadedAttachments). */
export function NoteComposer({ ticketId, onAdded, autoFocus = false, testid = 'ticket-note' }) {
  const [text, setText] = useState('');
  const [mentionIds, setMentionIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const body = text.trim();
    if ((!body && !files.length) || busy) return;
    setBusy(true);
    try {
      const uploaded = files.length ? await uploadTicketFiles(ticketId, files) : [];
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}/notes`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, attachment_ids: uploaded.map((a) => a.id), mention_user_ids: mentionIds }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Could not save note');
      const note = await res.json();
      onAdded?.(note, uploaded);
      setText(''); setMentionIds([]); setFiles([]);
      toast.success(note.mentions?.length ? `Note saved · pinged ${note.mentions.map((m) => m.name).join(', ')}` : 'Note saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`${testid}-composer`}>
      <NoteTemplateChips onPick={(t) => setText((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t))} testid={`${testid}-template`} />
      <MentionInput value={text} mentionIds={mentionIds} onChange={({ text: tx, mentionIds: ids }) => { setText(tx); setMentionIds(ids); }} placeholder="Add a note — type @ to ping a colleague" disabled={busy} autoFocus={autoFocus} testid={`${testid}-input`} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <NoteImagePicker files={files} setFiles={setFiles} testid={`${testid}-images`} />
        <button type="button" onClick={submit} disabled={busy || (!text.trim() && !files.length)} className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-slate-900 hover:bg-black rounded-md px-3 py-1.5 disabled:opacity-40" data-testid={`${testid}-submit`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Add note
        </button>
      </div>
    </div>
  );
}

/** Timeline of notes with the done-tick, used by the board drawer. */
export function NotesTimeline({ ticketId, notes, onToggleDone }) {
  if (!notes?.length) return <p className="text-sm text-slate-400 italic">No notes yet.</p>;
  return (
    <ul className="space-y-2">
      {notes.map((n) => (
        <li key={n.id} className={`rounded-lg border px-3 py-2 ${n.done_at ? 'border-emerald-100 bg-emerald-50/40' : 'border-slate-200 bg-white'}`} data-testid={`drawer-note-${n.id}`}>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
            <span className="font-semibold text-slate-800">{n.created_by_name || 'Staff'}</span>
            {n.kind && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-600 text-white">{n.kind === 'sms' ? 'Text' : 'Email'} → {n.to}</span>}
            <span>{new Date(n.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            {!n.kind && (
              <button type="button" onClick={() => onToggleDone(n)} className={`ml-auto inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${n.done_at ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300 text-slate-500 hover:border-emerald-500 hover:text-emerald-700'}`} data-testid={`drawer-note-done-${n.id}`}>
                <Check className="w-3 h-3" /> {n.done_at ? 'Done' : 'Mark done'}
              </button>
            )}
          </div>
          {n.body && <div className={`text-sm whitespace-pre-wrap mt-0.5 ${n.done_at ? 'text-slate-400 line-through' : 'text-slate-800'}`}>{renderWithMentions(n.body)}</div>}
          {n.attachments?.length > 0 && <div className="mt-1.5"><AttachmentGallery ticketId={ticketId} attachments={n.attachments} size="sm" testPrefix="drawer-note-att" /></div>}
          {n.mentions?.length > 0 && <div className="text-[10px] text-slate-500 mt-0.5">Emailed {n.mentions.map((m) => m.name).join(', ')}</div>}
        </li>
      ))}
    </ul>
  );
}

/** Side drawer opened from a board card: full notes history + composer, no navigation needed. */
export function TicketNotesDrawer({ ticket, onClose, onTicketChange }) {
  const open = !!ticket;
  const toggleDone = async (n) => {
    try {
      const res = await fetch(`${API_URL}/api/support/tickets/${ticket.id}/notes/${n.id}/done`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ done: !n.done_at }) });
      if (!res.ok) throw new Error('Could not update note');
      const updated = await res.json();
      onTicketChange({ ...ticket, notes: (ticket.notes || []).map((x) => (x.id === n.id ? updated : x)) });
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="ticket-notes-drawer">
        {ticket && (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="flex items-center gap-2 text-base"><StickyNote className="w-4 h-4" /> Notes · {ticket.reference || ticket.id.slice(0, 8).toUpperCase()}</SheetTitle>
              <SheetDescription className="text-xs">
                {ticket.title} · {ticket.installer_name}
                <Link to={`/staff/support/${ticket.id}`} className="ml-2 inline-flex items-center gap-1 text-[#7A0B7A] underline" data-testid="drawer-open-ticket"><ExternalLink className="w-3 h-3" /> open full ticket</Link>
              </SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <NoteComposer ticketId={ticket.id} autoFocus testid="drawer-note" onAdded={(note, uploaded) => onTicketChange({ ...ticket, notes: [...(ticket.notes || []), note], attachments: [...(ticket.attachments || []), ...uploaded] })} />
              <NotesTimeline ticketId={ticket.id} notes={[...(ticket.notes || [])].reverse()} onToggleDone={toggleDone} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
